// BUILD=R28-batch-resilience-and-button-counts
import { useMemo, useState } from "react";
import { Loader2, Printer, FileCheck2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { usePermissions } from "@/context/PermissionsContext";
import {
  useOrdersDecoprof,
  useUpdateOrdersDecoprofStatus,
  type OrdersDecoprofRow,
} from "@/hooks/useOrdersDecoprof";
import {
  ORDER_DECOPROF_TYPE_OPTIONS,
  printPackingSlipsBatch,
  type OrderDecoprofType,
} from "@/lib/ordersDecoprof";
import OrdersDecoprofMultiSelect from "./OrdersDecoprofMultiSelect";
import BadgeStatus3D, { BadgeType3D } from "./BadgeStatus3D";
import Card3D from "./Card3D";

const OrdersDecoprofTab = () => {
  const { data: orders, isLoading, error } = useOrdersDecoprof();
  const updateStatus = useUpdateOrdersDecoprofStatus();
  const { hasPermission } = usePermissions();

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [typeFilter, setTypeFilter] = useState<OrderDecoprofType[]>([]);
  const [supplierFilter, setSupplierFilter] = useState<string[]>([]);

  const allSuppliers = useMemo(() => {
    const set = new Set<string>();
    (orders ?? []).forEach((o) => o.primarySupplier && set.add(o.primarySupplier));
    return Array.from(set).sort();
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let list = orders ?? [];
    if (typeFilter.length > 0) list = list.filter((o) => typeFilter.includes(o.type));
    if (supplierFilter.length > 0) {
      list = list.filter((o) => o.primarySupplier && supplierFilter.includes(o.primarySupplier));
    }
    return list;
  }, [orders, typeFilter, supplierFilter]);

  const toggleRow = (ordernumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(ordernumber)) next.delete(ordernumber);
      else next.add(ordernumber);
      return next;
    });
  };

  const selectedRows: OrdersDecoprofRow[] = useMemo(
    () => (orders ?? []).filter((o) => selected.has(o.ordernumber) && o.orderid != null),
    [orders, selected],
  );

  const canPrintPackingSlip = hasPermission("ordersDecoprof", "read");
  const hasSelection = selected.size >= 1;
  const printDisabled = !canPrintPackingSlip || !hasSelection;
  const printDisabledReason = !canPrintPackingSlip
    ? "Geen toegang tot Orders Decoprof"
    : !hasSelection
      ? "Selecteer minimaal 1 order"
      : "";

  const [isPrinting, setIsPrinting] = useState(false);
  const [isMarkingCreated, setIsMarkingCreated] = useState(false);

  const handlePrint = async () => {
    if (selectedRows.length === 0) return;
    setIsPrinting(true);
    try {
      const orderids = selectedRows.map((o) => o.orderid as number);
      const result = await printPackingSlipsBatch(orderids);

      // Status update voor geslaagde orders
      const succeededRows = selectedRows.filter((o) =>
        result.succeededOrderids.includes(o.orderid as number),
      );
      let statusUpdated = 0;
      const statusFailed: string[] = [];
      for (const order of succeededRows) {
        try {
          await updateStatus.mutateAsync({
            ordernumber: order.ordernumber,
            status: "Uitgeprint",
          });
          statusUpdated++;
        } catch (e: any) {
          statusFailed.push(`${order.ordernumber} (${e?.message ?? e})`);
        }
      }

      const failedNumbers = selectedRows
        .filter((o) => result.failedOrderids.includes(o.orderid as number))
        .map((o) => o.ordernumber);

      if (failedNumbers.length === 0 && statusFailed.length === 0) {
        toast.success(
          `Pakbon-PDF gedownload — ${statusUpdated} order${statusUpdated === 1 ? "" : "s"} gemarkeerd als Uitgeprint`,
        );
      } else if (failedNumbers.length > 0) {
        toast.warning(
          `${succeededRows.length} verwerkt, ${failedNumbers.length} overgeslagen: ${failedNumbers.join(", ")}`,
        );
      } else if (statusFailed.length > 0) {
        toast.warning(`PDF gedownload, maar status-update faalde voor: ${statusFailed.join(", ")}`);
      }
    } catch (e: any) {
      console.error("[batch-print] error", e);
      const reasons = e?.failedReasons as Record<string, string> | undefined;
      const detail = reasons && Object.keys(reasons).length > 0
        ? ` — ${Object.entries(reasons).map(([id, r]) => `${id}: ${r}`).join("; ")}`
        : "";
      toast.error(`Pakbon afdrukken mislukt: ${e?.message ?? e}${detail}`);
    } finally {
      setIsPrinting(false);
    }
  };

  const handleMarkCreated = async () => {
    if (selectedRows.length === 0) return;
    setIsMarkingCreated(true);
    const failed: string[] = [];
    let succeeded = 0;
    try {
      for (const order of selectedRows) {
        try {
          await updateStatus.mutateAsync({
            ordernumber: order.ordernumber,
            status: "Aangemaakt",
          });
          succeeded++;
        } catch (e: any) {
          failed.push(`${order.ordernumber} (${e?.message ?? e})`);
        }
      }
      if (failed.length === 0) {
        toast.success(`${succeeded} order${succeeded === 1 ? "" : "s"} gemarkeerd als Aangemaakt`);
      } else {
        toast.warning(`${succeeded} ok, ${failed.length} mislukt: ${failed.join(", ")}`);
      }
    } finally {
      setIsMarkingCreated(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <OrdersDecoprofMultiSelect
          label="Type order"
          options={ORDER_DECOPROF_TYPE_OPTIONS as readonly string[]}
          values={typeFilter}
          onChange={(v) => setTypeFilter(v as OrderDecoprofType[])}
        />
        <OrdersDecoprofMultiSelect
          label="Leverancier"
          options={allSuppliers}
          values={supplierFilter}
          onChange={setSupplierFilter}
        />

        <div className="ml-auto flex gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    variant="outline"
                    onClick={handleMarkCreated}
                    disabled={printDisabled || updateStatus.isPending || isMarkingCreated || isPrinting}
                    className="gap-2 min-w-[14rem] justify-center"
                  >
                    {isMarkingCreated ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <FileCheck2 className="h-4 w-4" />
                    )}
                    {selected.size === 0
                      ? "Aangemaakt"
                      : selected.size === 1
                        ? "1 order op Aangemaakt"
                        : `${selected.size} orders op Aangemaakt`}
                  </Button>
                </span>
              </TooltipTrigger>
              {printDisabledReason && <TooltipContent>{printDisabledReason}</TooltipContent>}
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handlePrint}
                    disabled={printDisabled || updateStatus.isPending || isPrinting || isMarkingCreated}
                    className="gap-2 min-w-[12rem] justify-center"
                  >
                    {isPrinting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Printer className="h-4 w-4" />
                    )}
                    {selected.size === 0
                      ? "Pakbon afdrukken"
                      : selected.size === 1
                        ? "1 order afdrukken"
                        : `${selected.size} orders afdrukken`}
                  </Button>
                </span>
              </TooltipTrigger>
              {printDisabledReason && <TooltipContent>{printDisabledReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {/* Resultaten-aantal */}
      {!isLoading && (
        <p className="text-sm text-muted-foreground">
          {filteredOrders.length} order{filteredOrders.length !== 1 ? "s" : ""} gevonden
          {selected.size > 0 && ` — ${selected.size} geselecteerd`}
        </p>
      )}

      {/* Header-rij (kolomtitels), buiten de tegels — visueel uitgelijnd op grid */}
      <div className="grid grid-cols-[40px_1fr_180px_1fr_140px] gap-3 px-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <div></div>
        <div>Ordernummer</div>
        <div>Type order</div>
        <div>Leverancier</div>
        <div>Status</div>
      </div>

      {/* Per-rij 3D-tegels */}
      <div className="space-y-2">
        {isLoading ? (
          <Card3D className="px-4 py-8 text-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
            Laden...
          </Card3D>
        ) : error ? (
          <Card3D className="px-4 py-8 text-center text-destructive">
            Fout bij laden van orders
          </Card3D>
        ) : filteredOrders.length === 0 ? (
          <Card3D className="px-4 py-8 text-center text-muted-foreground">
            Geen Decoprof-orders gevonden
          </Card3D>
        ) : (
          filteredOrders.map((order) => (
            <Card3D
              key={order.ordernumber}
              className="grid grid-cols-[40px_1fr_180px_1fr_140px] items-center gap-3 px-4 py-3 transition-transform duration-150 hover:-translate-y-0.5"
            >
              <div>
                <Checkbox
                  checked={selected.has(order.ordernumber)}
                  onCheckedChange={() => toggleRow(order.ordernumber)}
                  aria-label={`Selecteer order ${order.ordernumber}`}
                />
              </div>
              <div className="font-medium">{order.ordernumber}</div>
              <div>
                <BadgeType3D type={order.type} />
              </div>
              <div className="text-muted-foreground truncate">
                {order.primarySupplier ?? "Onbekend"}
              </div>
              <div>
                <BadgeStatus3D
                  status={order.status}
                  interactive={order.status !== "Aangemaakt" && hasPermission("ordersDecoprof", "write")}
                  title={
                    order.status === "Aangemaakt"
                      ? "Reeds aangemaakt"
                      : "Klik om status naar Aangemaakt te zetten"
                  }
                  onClick={() => {
                    if (order.status === "Aangemaakt") return;
                    updateStatus.mutate(
                      { ordernumber: order.ordernumber, status: "Aangemaakt" },
                      {
                        onSuccess: () => toast.success(`${order.ordernumber} → Aangemaakt`),
                        onError: (e: any) =>
                          toast.error(`Status update mislukt: ${e?.message ?? e}`),
                      },
                    );
                  }}
                />
              </div>
            </Card3D>
          ))
        )}
      </div>
    </div>
  );
};

export default OrdersDecoprofTab;
